// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import { StyledTypography } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { mainNavigationStyles } from "@pace/Overrides/Widgets/Header/MainNavigation";
import IconArrowDown from "@pace/XDL/Icons/IconArrowDown";
import breakpoints from "@pace/XDL/PreStyle/breakpoints";
import { fontConfig } from "@pace/XDL/PreStyle/typography";
import { color, getSystemColor, styledScrollbar, xScreen } from "@pace/XDL/Utilities";
import styled, { css } from "styled-components";

mainNavigationStyles.container = {
    css: css`
        color: ${getColor("common.background")};
        padding: 0;
        display: flex;
        justify-content: left;
        column-gap: 24px;
        ${xScreen(1024, 1330)} {
            column-gap: 12px;
        }
    `,
};

mainNavigationStyles.menuItem = {
    css: css`
        display: flex;
        align-items: center;
        height: 30px;
        padding: 4px 8px;
        border-radius: 4px;

        ${TypographyStyle} {
            color: ${color("common.ui14")};
            display: flex;
            align-items: center;
        }
        ${IconWrapper} {
            color: ${color("secondary")};
            margin-right: 0;
        }
        &:focus {
            outline: none;
        }
    `,
};

mainNavigationStyles.nonClickabkeMenuItem = {
    color: "common.ui14",
    weight: 700,
    size: 14,
    css: css`
        display: flex;
        align-items: center;   
        text-transform: uppercase;
        padding: 4px 8px;
        &:hover {
            background-color: ${color("common.ui11")};
            border-radius: 4px;
            color: ${color("primary")};
            ${IconWrapper} {
                transform: rotate(180deg);
            }
        }
    `
}

mainNavigationStyles.itemWrapper = {
    css: css`
        position: relative;
        overflow: visible;
        display: flex;
        &:hover span + div {
            display: block;
        }
    `,
};

mainNavigationStyles.menuItemTypography = {
    variant: "headerSecondary",
    lineHeight: "22px",
    css: css`
        color: ${getSystemColor("common.ui14")};
    `,
};

mainNavigationStyles.menuItemIcon = {
    src: IconArrowDown,
    color: "secondary",
    size: 9,
    css: css`
        margin-left: 9px;
        margin-bottom: 1px;
        svg {
            height: auto;
        }
    `,
};

mainNavigationStyles.megaMenu = {
    css: css`
        display: flex;
        background-color: ${color("common.ui1")};
        border: 1px solid ${color("common.ui12")};
        border-radius: 0 0 4px 4px;
        max-height: 532px;
        max-width: 1334px;
        width: 100%;
    `,
};

mainNavigationStyles.megaMenuLink = {
    typographyProps: {
        size: 14,
        lineHeight: "22px",
        weight: fontConfig.weight.regular,
    },
    css: css`
        padding: 6px 8px;
        width: 100%;
        ${TypographyStyle} {
            color: ${color("common.ui6")};
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: nowrap;
            width: 100%;
            > ${TypographyStyle} {
                display: inline-block;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        ${IconWrapper} {
            margin: 0;
            &,
            svg {
                width: auto;
            }
        }
    `,
};

mainNavigationStyles.megaMenuFirstColumn = {
    css: css`
        border-bottom-left-radius: 4px;
        border: 1px solid ${color("common.ui12")};
        background-color: ${color("common.ui1")};
        display: flex;
        flex-direction: column;
        width: 265px;
        padding: 10px 0;
        height: 532px;
        max-height: calc(100vh - 150px);

        > [data-tippy-root] {
            display: flex;
            height: 532px;
            max-height: calc(100vh - 150px);
            max-width: calc(${breakpoints.maxWidths[3]}px - 265px);
            width: fit-content;

            ${xScreen("gte", "xl")} {
                max-width: calc(${breakpoints.maxWidths[4]}px - 265px);
            }
        }
    `,
};

mainNavigationStyles.megaMenuLevel1 = {
    css: css`
        height: 100%;
        overflow-y: auto;
        ${styledScrollbar()};
        width: calc(100% - 8px);
        margin: auto;
    `,
};

mainNavigationStyles.megaMenuSecondColumn = {
    css: css`
        background-color: ${color("common.ui1")};
        border: 1px solid ${color("common.ui12")};
        display: flex;
        height: 532px;
        max-height: calc(100vh - 150px);
        flex: 1 0 755px;
        width: fit-content;
        padding: 16px 12px;
        background-color: ${color("common.ui1")};
        overflow-y: auto;
        ${styledScrollbar()};
    `,
};

mainNavigationStyles.megaMenuLevel2 = {
    css: css`
        background-color: ${color("common.ui1")};
        column-gap: 22px;
        column-count: 3;
        column-gap: 20px;
        width: 100%;
    `,
};

mainNavigationStyles.megaMenuLevel2Section = {
    css: css`
        display: grid;
        grid-template-rows: 1fr auto;
        margin-bottom: 20px;
        break-inside: avoid;
    `,
};

mainNavigationStyles.megaMenuLevel2Link = {
    typographyProps: {
        size: 14,
        lineHeight: "22px",
        weight: fontConfig.weight.bold,
    },
    css: css`
        margin-bottom: 8px;
        ${StyledTypography} {
            color: ${color("common.ui6")};
        }
    `,
};

mainNavigationStyles.megaMenuLevel3Link = {
    typographyProps: {
        size: 14,
        lineHeight: "22px",
        weight: fontConfig.weight.regular,
    },
    css: css`
        margin-bottom: 8px;
        ${StyledTypography} {
            color: ${color("common.ui6")};
        }
    `,
};

mainNavigationStyles.megaMenuBrands = {
    css: css`
        background-color: ${color("common.ui11")};
        padding: 16px 12px;
        flex: 0 0 317px;
        border: 1px solid ${color("common.ui12")};
        border-left: 0;
        border-bottom-right-radius: 4px;
        overflow-y: auto;
        ${styledScrollbar()};
    `,
};

mainNavigationStyles.shopBrandsTitle = {
    wrapper: {
        css: css`
            margin-bottom: 20px;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    title: {
        size: 18,
        css: css`
            line-height: 22px;
            margin-bottom: 8px;
        `,
    },
    link: {
        typographyProps: {
            size: 12,
            lineHeight: "13px",
            weight: fontConfig.weight.bold,
        },
        icon: {
            iconProps: {
                css: css`
                    svg {
                        width: 11px;
                        height: 13px;
                    }
                    opacity: 0.5;
                `,
            },
        },
        css: css`
            ${IconWrapper} {
                margin-left: 8px;
            }
            margin-bottom: 0;
            align-items: center;
        `,
        mergeCss: true,
    },
};

mainNavigationStyles.brandsWrapper = {
    css: css`
        display: flex;
        gap: 15px;
        justify-content: flex-start;
        flex-wrap: wrap;
    `,
};

mainNavigationStyles.brandItem = {
    css: css`
        height: 87px;
        width: 87px;
        border-radius: 4px;
        background-color: ${color("common.ui1")};
        padding: 8px;
    `,
};

mainNavigationStyles.brandImage = {
    css: css`
        max-width: 100%;
        max-height: 100%;
        height: auto;
    `,
};

mainNavigationStyles.cascadingMenu = {
    cssOverrides: {
        menuWrapper: css`
            background-color: ${color("common.ui1")};
            border: 1px solid ${color("common.ui12")};
            border-radius: 0 0 4px 4px;
            min-height: 80px;
            max-height: 532px;
            padding: 10px 4px;
            height: 100%;
            overflow-y: auto;
            ${styledScrollbar()};
            > div {
                position: static;
            }
        `,
    },
    menuItemLinkProps: {
        typographyProps: {
            size: 14,
            lineHeight: "22px",
            weight: fontConfig.weight.regular,
            ellipsis: true,
        },
        css: css`
            padding: 6px 8px;
            width: 100%;
            ${TypographyStyle} {
                color: ${color("common.ui6")};
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: nowrap;
                white-space: wrap;
                width: 100%;
            }

            ${IconWrapper} {
                margin: 0;
                &,
                svg {
                    width: auto;
                }
            }
        `,
    },
    nonClickableMenuItemProps: {
        size: 14,
        lineHeight: "22px",
        weight: fontConfig.weight.regular,
        ellipsis: true,
        color: "common.ui6",

        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: nowrap;
            white-space: wrap;
            padding: 6px 8px;
            width: 100%;
            border-radius: 4px;

            ${IconWrapper} {
                margin: 0;
                &,
                svg {
                    width: auto;
                }
            }
        `,
    },
};

export const MenuItemTrigger = styled.div`
    &[aria-expanded="true"] ${StyledButton}, ${StyledButton}:focus {
        background: ${color("common.ui11")};
        ${TypographyStyle} {
            color: ${color("primary")};
        }
        ${IconWrapper} {
            transform: rotate(180deg);
            color: ${color("secondary")};
        }
    }
`;

export const InnerMenuItemTrigger = styled.div`
    &[aria-expanded="true"], &:hover, &:focus {
        ${StyledButton} {
            background-color: ${color("common.ui11")};
            border-radius: 4px;

            ${TypographyStyle} {
                font-weight: ${fontConfig.weight.bold};
                color: ${color("primary")};
            }

            ${IconWrapper} {
                color: ${color("primary")};
            }
        }

        .mega-menu-heading-link.non-clickable {
            background-color: ${color("common.ui11")};
            border-radius: 4px;
            font-weight: ${fontConfig.weight.bold};
            color: ${color("primary")};

            ${IconWrapper} {
                color: ${color("primary")};
            }
        }
        ~ div[data-tippy-root] {
            height: 100%;
        }
    }
`;
